import { useEffect, useState } from "react";
import { useUser } from "../../auth/UserContext.js";
import { TxnRow } from "../../components/TxnRow.js";
import { fmt } from "../../lib/money.js";
import { listTxns, type Txn } from "../../api/txns.js";
import type { KidBundle } from "../../api/me.js";

interface Group {
  month: string;
  txns: Txn[];
}

function groupByMonth(txns: Txn[]): Group[] {
  const order: string[] = [];
  const map: Record<string, Txn[]> = {};
  for (const t of txns) {
    const d = new Date(t.date);
    const key = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    if (!(key in map)) {
      map[key] = [];
      order.push(key);
    }
    map[key]!.push(t);
  }
  return order.map((month) => ({ month, txns: map[month] ?? [] }));
}

export default function History() {
  const { bundle } = useUser();
  const [allTxns, setAllTxns] = useState<Txn[]>([]);
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [seeded, setSeeded] = useState(false);
  const [fetchedOnce, setFetchedOnce] = useState(false);

  useEffect(() => {
    if (bundle && bundle.user.role === "kid" && !seeded) {
      setAllTxns((bundle as KidBundle).recentTxns ?? []);
      setSeeded(true);
    }
  }, [bundle, seeded]);

  if (!bundle || bundle.user.role !== "kid") return null;
  const kid = bundle as KidBundle;

  const loadMore = async () => {
    setLoading(true);
    try {
      const opts: { continuationToken?: string } = {};
      if (continuationToken !== null) opts.continuationToken = continuationToken;
      const res = await listTxns(kid.user.rowKey, opts);
      setAllTxns((prev) => {
        // Dedupe by rowKey
        const seen = new Set(prev.map((t) => t.rowKey));
        const fresh = res.rows.filter((t) => !seen.has(t.rowKey));
        return [...prev, ...fresh];
      });
      setContinuationToken(res.continuationToken);
      setFetchedOnce(true);
    } finally {
      setLoading(false);
    }
  };

  const groups = groupByMonth(allTxns);
  const hasMore = !fetchedOnce || continuationToken !== null;

  return (
    <div>
      <h1 style={{ marginBottom: 6 }}>Transaction history</h1>
      <p className="soft" style={{ marginBottom: 24 }}>
        Every dollar coming in and going out.
      </p>

      {groups.map(({ month, txns }) => {
        const inflow = txns.filter((t) => t.amountCents > 0).reduce((s, t) => s + t.amountCents, 0);
        const outflow = txns
          .filter((t) => t.amountCents < 0)
          .reduce((s, t) => s + t.amountCents, 0);
        return (
          <div key={month} className="card" style={{ marginBottom: 16 }}>
            <div className="row-between" style={{ marginBottom: 10 }}>
              <h3>{month}</h3>
              <div className="row" style={{ gap: 14, fontSize: 13 }}>
                <div className="money-pos">+{fmt(inflow)}</div>
                <div className="money-neg">{fmt(outflow)}</div>
              </div>
            </div>
            <div>
              {txns.map((t) => (
                <TxnRow key={t.rowKey} t={t} />
              ))}
            </div>
          </div>
        );
      })}

      {hasMore && (
        <div className="row" style={{ justifyContent: "center", marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={loadMore} disabled={loading}>
            {loading ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
